import { ScheduleFrame, ScheduleKind } from './desktop-metadata';

/**
 * The day of a dynamic wallpaper (2026-10-12): one composition (the wallpaper generator's style,
 * seed and fields), lit for each hour. The palette is relit per frame in OKLab: noon is the palette
 * itself, the hours around sunrise and sunset pick up a warm glow, the night dims it and turns it
 * moonlit blue. The sun's height drives all of it, so the time-of-day and the sun-position files
 * show the same pictures.
 */

export type DynamicMode = 'time' | 'solar' | 'appearance';

export interface DynamicModeInfo {
  key: DynamicMode;
  label: string;
  /** what macOS does with it, for the picker */
  blurb: string;
  /** the XMP attribute that carries the schedule */
  kind: ScheduleKind;
}

export const DYNAMIC_MODES: DynamicModeInfo[] = [
  { key: 'time', label: 'Time of Day', blurb: 'Changes through the day by the clock', kind: 'h24' },
  { key: 'solar', label: 'Sun Position', blurb: 'Follows the sun where you are, like the macOS dynamic wallpapers', kind: 'solar' },
  { key: 'appearance', label: 'Light & Dark', blurb: 'One picture for Light mode, one for Dark mode', kind: 'apr' },
];

/** the hours drawn for each frame count: denser around sunrise and sunset, where the light changes */
export const FRAME_HOURS: { [ count: number ]: number[] } = {
  4: [ 0, 6.5, 12, 18 ],
  8: [ 0, 5.5, 6.5, 9, 12, 15, 17.5, 19.5 ],
  12: [ 0, 3, 5.5, 6.25, 7, 9, 12, 15, 17, 17.75, 18.5, 21 ],
  16: [ 0, 2, 4, 5.25, 6, 6.75, 8, 10, 12, 14, 16, 17.25, 18, 18.75, 20, 22 ],
};

export const FRAME_COUNTS = Object.keys( FRAME_HOURS ).map( Number );

/** the sun the frames are drawn for: latitude 40°, an equinox day (sunrise at 6, sunset at 18) */
const LATITUDE = 40;
const DECLINATION = 0;

const rad = ( d: number ) => d * Math.PI / 180;
const deg = ( r: number ) => r * 180 / Math.PI;

/** the sun's altitude and azimuth (from north, clockwise) at a local solar hour */
export function sunAt( hour: number ): { altitude: number; azimuth: number } {
  const h = rad( 15 * ( hour - 12 ) );
  const phi = rad( LATITUDE ), delta = rad( DECLINATION );
  const altitude = Math.asin( Math.sin( phi ) * Math.sin( delta ) + Math.cos( phi ) * Math.cos( delta ) * Math.cos( h ) );
  let azimuth = deg( Math.atan2( Math.sin( h ), Math.cos( h ) * Math.sin( phi ) - Math.tan( delta ) * Math.cos( phi ) ) ) + 180;
  azimuth = ( azimuth + 360 ) % 360;
  return { altitude: deg( altitude ), azimuth };
}

export interface DynamicFrame extends ScheduleFrame {
  /** "06:30" */
  label: string;
  /** what the hour looks like: Night, Sunrise, Morning, ... */
  phase: string;
  /** the palette lit for this hour */
  colors: string[];
}

export interface DayLightOptions {
  /** how dark the night gets, 0..100 */
  night: number;
  /** how warm sunrise and sunset glow, 0..100 */
  glow: number;
}

export const DEFAULT_DAY_LIGHT: DayLightOptions = { night: 70, glow: 60 };

/** the frames of a mode, in file order, each with its relit palette */
export function dayFrames( mode: DynamicMode, count: number, colors: string[], light: DayLightOptions ): DynamicFrame[] {
  const hours = mode === 'appearance' ? [ 12, 0 ] : ( FRAME_HOURS[ count ] || FRAME_HOURS[ 8 ] );
  return hours.map( ( hour, index ) => {
    const sun = sunAt( hour );
    return {
      index, hour, altitude: sun.altitude, azimuth: sun.azimuth,
      label: hourLabel( hour ),
      phase: mode === 'appearance' ? ( index === 0 ? 'Light' : 'Dark' ) : phaseOf( hour, sun.altitude ),
      colors: colors.map( c => relight( c, sun.altitude, hour < 12, light ) ),
    };
  } );
}

/** the brightest frame is Light mode's, the darkest Dark mode's */
export function lightAndDark( frames: DynamicFrame[] ): { light: number; dark: number } {
  let light = 0, dark = 0;
  frames.forEach( ( f, i ) => {
    if ( f.altitude > frames[ light ].altitude ) light = i;
    if ( f.altitude < frames[ dark ].altitude ) dark = i;
  } );
  return { light, dark };
}

/**
 * The frames in the order the file keeps them: the Light frame first, then the rest by the clock, each
 * re-indexed to its place. macOS (as Apple's own dynamic wallpapers have it) reads the schedule from the
 * first picture and shows that picture where it does not follow one; a file that began at midnight was a
 * night picture at 13:00 (owner, 2026-10-13).
 */
export function fileOrder( frames: DynamicFrame[] ): DynamicFrame[] {
  if ( !frames.length ) return [];
  const { light } = lightAndDark( frames );
  return [ frames[ light ], ...frames.filter( ( _f, i ) => i !== light ) ].map( ( f, index ) => ( { ...f, index } ) );
}

export function hourLabel( hour: number ): string {
  const minutes = Math.round( ( ( hour % 24 ) + 24 ) % 24 * 60 );
  const hh = Math.floor( minutes / 60 ) % 24, mm = minutes % 60;
  return `${ hh < 10 ? '0' : '' }${ hh }:${ mm < 10 ? '0' : '' }${ mm }`;
}

function phaseOf( hour: number, altitude: number ): string {
  if ( altitude < -12 ) return 'Night';
  if ( altitude < -2 ) return hour < 12 ? 'Dawn' : 'Dusk';
  if ( altitude < 10 ) return hour < 12 ? 'Sunrise' : 'Sunset';
  if ( altitude < 35 ) return hour < 12 ? 'Morning' : 'Afternoon';
  return 'Noon';
}

const smooth = ( a: number, b: number, x: number ) => {
  const t = Math.max( 0, Math.min( 1, ( x - a ) / ( b - a ) ) );
  return t * t * ( 3 - 2 * t );
};

// the warm light at the horizon: rosy in the morning, amber in the evening (OKLab a, b)
const DAWN = labOf( '#ff7a8a' );
const DUSK = labOf( '#ff8a3d' );
// the moonlight the night leans to
const MOON = labOf( '#1e3a8a' );

/** how strongly each light acts at one sun height; null at noon, where the picture is itself */
interface Lighting {
  night: number;
  deep: number;
  glow: number;
  lowSun: number;
  morning: boolean;
}

function lightingAt( altitude: number, morning: boolean, light: DayLightOptions ): Lighting | null {
  const day = smooth( -10, 16, altitude );
  const deep = smooth( -12, -45, altitude );
  const night = ( 1 - day ) * ( 0.35 + 0.65 * clamp01( light.night / 100 ) );
  // the glow peaks with the sun on the horizon and is gone 16° either side of it
  const near = Math.max( 0, 1 - Math.abs( altitude - 1 ) / 16 );
  const glow = near * near * clamp01( light.glow / 100 );
  // a low daytime sun is a little warm too, so the morning and the afternoon are not noon again
  const lowSun = day * ( 1 - smooth( 12, 48, altitude ) ) * 0.12 * clamp01( light.glow / 100 );
  return night === 0 && glow === 0 && lowSun === 0 ? null : { night, deep, glow, lowSun, morning };
}

function relightLab( lab: [ number, number, number ], k: Lighting ): [ number, number, number ] {
  let [ L, a, b ] = lab;
  // golden hour: the hue pulled toward the warm light, a touch brighter where it is dark
  if ( k.glow > 0 ) {
    const warm = k.morning ? DAWN : DUSK;
    const g = 0.45 * k.glow;
    a += ( warm[ 1 ] - a ) * g;
    b += ( warm[ 2 ] - b ) * g;
    L += ( Math.max( L, 0.55 ) - L ) * g * 0.35;
  }
  if ( k.lowSun > 0 ) {
    const warm = k.morning ? DAWN : DUSK;
    a += ( warm[ 1 ] - a ) * k.lowSun;
    b += ( warm[ 2 ] - b ) * k.lowSun;
  }
  // night: darker, and the hue pulled toward the moonlight (only dimming turns a yellow olive),
  // a little more so past the blue hour
  if ( k.night > 0 ) {
    L *= 1 - 0.62 * k.night - 0.08 * k.deep * k.night;
    const m = 0.75 * k.night;
    a += ( MOON[ 1 ] * 0.7 - a ) * m;
    b += ( MOON[ 2 ] * 0.7 - b ) * m;
  }
  return [ L, a, b ];
}

/** one palette color as it looks with the sun at `altitude` degrees */
export function relight( hex: string, altitude: number, morning: boolean, light: DayLightOptions ): string {
  const k = lightingAt( altitude, morning, light );
  if ( !k ) {
    // noon is the palette itself, not a round trip through OKLab
    return '#' + hex.replace( '#', '' ).toLowerCase();
  }
  const [ L, a, b ] = relightLab( labOf( hex ), k );
  return hexOf( L, a, b );
}

// ---------------------------------------------------------------- the light on a whole picture

/** the lattice of the light's colour table: 17 steps an RGB channel (the light is smooth, trilinear does the rest) */
const TABLE = 17;

/**
 * The light of one hour as a colour table over the RGB cube (null at noon). The frames are the noon
 * picture with this light on every pixel, not the picture drawn again in darker colours: a style that
 * brightens a dark palette for contrast (Circuit lifts its board) would turn the night light.
 */
export function lightTable( altitude: number, morning: boolean, light: DayLightOptions ): Uint8ClampedArray | null {
  const k = lightingAt( altitude, morning, light );
  if ( !k ) return null;
  const out = new Uint8ClampedArray( TABLE * TABLE * TABLE * 3 );
  const lin: number[] = [];
  for ( let i = 0; i < TABLE; i++ ) lin.push( toLinear( Math.round( i * 255 / ( TABLE - 1 ) ) ) );
  let i = 0;
  for ( let r = 0; r < TABLE; r++ ) {
    for ( let g = 0; g < TABLE; g++ ) {
      for ( let b = 0; b < TABLE; b++ ) {
        const [ L, A, B ] = relightLab( labOfLinear( lin[ r ], lin[ g ], lin[ b ] ), k );
        const [ R, G, Bl ] = rgbOf( L, A, B );
        out[ i++ ] = R;
        out[ i++ ] = G;
        out[ i++ ] = Bl;
      }
    }
  }
  return out;
}

/** the table on every pixel of an RGBA picture, in place (trilinear between the lattice points) */
export function applyLightTable( data: Uint8ClampedArray, table: Uint8ClampedArray ): void {
  const N = TABLE, S = ( N - 1 ) / 255;
  // strides in the table for one step of r, g and b
  const sr = N * N * 3, sg = N * 3, sb = 3;
  // each channel value's lattice cell and the weight within it, once for all 256 values
  const cell = new Int32Array( 256 ), frac = new Float32Array( 256 );
  for ( let v = 0; v < 256; v++ ) {
    const f = v * S, c = Math.min( N - 2, f | 0 );
    cell[ v ] = c;
    frac[ v ] = f - c;
  }
  const t = table;
  for ( let p = 0; p < data.length; p += 4 ) {
    const R = data[ p ], G = data[ p + 1 ], B = data[ p + 2 ];
    const dr = frac[ R ], dg = frac[ G ], db = frac[ B ];
    const o = cell[ R ] * sr + cell[ G ] * sg + cell[ B ] * sb;
    const o1 = o + sb, o2 = o + sg, o3 = o2 + sb, o4 = o + sr, o5 = o4 + sb, o6 = o4 + sg, o7 = o6 + sb;
    for ( let ch = 0; ch < 3; ch++ ) {
      const x00 = t[ o + ch ] + ( t[ o1 + ch ] - t[ o + ch ] ) * db;
      const x01 = t[ o2 + ch ] + ( t[ o3 + ch ] - t[ o2 + ch ] ) * db;
      const x10 = t[ o4 + ch ] + ( t[ o5 + ch ] - t[ o4 + ch ] ) * db;
      const x11 = t[ o6 + ch ] + ( t[ o7 + ch ] - t[ o6 + ch ] ) * db;
      const y0 = x00 + ( x01 - x00 ) * dg;
      data[ p + ch ] = y0 + ( x10 + ( x11 - x10 ) * dg - y0 ) * dr;
    }
  }
}

function clamp01( v: number ): number {
  return Math.max( 0, Math.min( 1, isNaN( v ) ? 0 : v ) );
}

// ---------------------------------------------------------------- OKLab (Björn Ottosson)

function toLinear( c: number ): number {
  c /= 255;
  return c <= 0.04045 ? c / 12.92 : Math.pow( ( c + 0.055 ) / 1.055, 2.4 );
}

function toGamma( c: number ): number {
  const v = c <= 0.0031308 ? 12.92 * c : 1.055 * Math.pow( c, 1 / 2.4 ) - 0.055;
  return Math.max( 0, Math.min( 255, Math.round( v * 255 ) ) );
}

export function labOf( hex: string ): [ number, number, number ] {
  const h = hex.replace( '#', '' );
  const n = parseInt( h.length === 3 ? h.split( '' ).map( c => c + c ).join( '' ) : h, 16 );
  return labOfLinear( toLinear( ( n >> 16 ) & 255 ), toLinear( ( n >> 8 ) & 255 ), toLinear( n & 255 ) );
}

function labOfLinear( r: number, g: number, bl: number ): [ number, number, number ] {
  const l = Math.cbrt( 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * bl );
  const m = Math.cbrt( 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * bl );
  const s = Math.cbrt( 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * bl );
  return [
    0.2104542553 * l + 0.7936177850 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.4285922050 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.8086757660 * s,
  ];
}

export function hexOf( L: number, a: number, b: number ): string {
  return '#' + rgbOf( L, a, b ).map( v => ( v < 16 ? '0' : '' ) + v.toString( 16 ) ).join( '' );
}

function rgbOf( L: number, a: number, b: number ): [ number, number, number ] {
  const l = Math.pow( L + 0.3963377774 * a + 0.2158037573 * b, 3 );
  const m = Math.pow( L - 0.1055613458 * a - 0.0638541728 * b, 3 );
  const s = Math.pow( L - 0.0894841775 * a - 1.2914855480 * b, 3 );
  return [
    toGamma( 4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s ),
    toGamma( -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s ),
    toGamma( -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s ),
  ];
}
